import { LayoutGrid, MessageSquare } from 'lucide-react';

import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import type { SidebarMode } from '@/lib/sidebar-mode';
import { cn } from '@/lib/utils';

const options: {
  value: SidebarMode;
  label: string;
  icon: typeof LayoutGrid;
}[] = [
  { value: 'browse', label: 'Browse', icon: LayoutGrid },
  { value: 'chat', label: 'Chat', icon: MessageSquare },
];

/**
 * Two-option segmented control at the top of the app sidebar (ENG-1161):
 * "Browse" shows the navigation, "Chat" shows Claire's chat history.
 *
 * A single-select `ToggleGroup`, so it is announced as a radio group
 * ("Sidebar view") with `aria-checked` on the selected option. Radix emits an
 * empty value when the already-selected option is clicked again; that is
 * ignored so the control can never end up with nothing selected.
 */
export function SidebarModeToggle({
  mode,
  onModeChange,
  className,
}: {
  mode: SidebarMode;
  onModeChange: (mode: SidebarMode) => void;
  className?: string;
}) {
  return (
    <ToggleGroup
      aria-label="Sidebar view"
      className={cn(
        'bg-sidebar-accent/60 grid w-full grid-cols-2 gap-0.5 rounded-lg p-0.5',
        className
      )}
      onValueChange={(value) => {
        if (value === 'browse' || value === 'chat') onModeChange(value);
      }}
      size="sm"
      type="single"
      value={mode}
    >
      {options.map(({ value, label, icon: Icon }) => (
        <ToggleGroupItem
          aria-label={label}
          className={cn(
            // Override the group's joined-edge styling: this is a pill switch,
            // each option fully rounded, the selected one lifted onto a card.
            'h-7 w-full gap-1.5 rounded-md! px-2 text-xs font-medium text-sidebar-foreground/70',
            'hover:bg-transparent hover:text-sidebar-foreground',
            'data-[state=on]:bg-background data-[state=on]:text-sidebar-foreground data-[state=on]:shadow-sm'
          )}
          key={value}
          value={value}
        >
          <Icon className="size-3.5" />
          {label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}
